import { ONE_TIME_CONTRACT_STATUSES } from "@/constants";
import { SERVICE_CATEGORIES, COPYRIGHT_TYPES } from "@/constants/oneTimeContract";
import { getClients } from "@/service/weexpertService";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { BaseDialog } from "./dialog";
import MultipleSelector from "../ui/multiple-selector";
import { DatePicker } from "../ui/date-picker";
import { format } from "date-fns";
import { useQuery } from "react-query";
import { CheckIcon } from "@radix-ui/react-icons";
import MultipleSelectorWithSearch from "../ui/multiple-selector-with-search";
import { Loader } from "../ProtectedRoute";
import { getUser } from "@/service/weexpertService";

const transformAutocompleteValueToFilter = (valueArray, field, key = 'id') => {
  return {
    field: field,
    value: valueArray.map(e => e[key])
  }
}

export const processOneTimeContractFilter = (unprocessedFilter) => {
  const {
    selectedClients = [],
    selectedStatus = [],
    selectedServiceCategory = [],
    selectedCopyrightType = [],
    createdAtPeriod = [],
    paymentDueDatePeriod = []
  } = unprocessedFilter;
  const clientsFilter = transformAutocompleteValueToFilter(selectedClients, 'client');
  const statusFilter = transformAutocompleteValueToFilter(selectedStatus, 'status', 'value');
  const serviceCategoryFilter = transformAutocompleteValueToFilter(selectedServiceCategory, 'serviceCategory', 'value');
  const copyrightTypeFilter = transformAutocompleteValueToFilter(selectedCopyrightType, 'copyrightType', 'value');
  
  return {
    client: clientsFilter,
    status: statusFilter,
    serviceCategory: serviceCategoryFilter,
    copyrightType: copyrightTypeFilter,
    createdAt: {
      field: 'createdAt',
      value: createdAtPeriod.filter(e => e).length == 2 ?
        createdAtPeriod.filter(e => e).map(e => format(new Date(e), 'yyyy-MM-dd')) : []
    },
    paymentDueDate: {
      field: 'paymentDueDate',
      value: paymentDueDatePeriod.filter(e => e).length == 2 ?
        paymentDueDatePeriod.filter(e => e).map(e => format(new Date(e), 'yyyy-MM-dd')) : []
    },
  }
}

export const OneTimeContractFilter = ({ onFilterChange, currentFilter }) => {

  const { t } = useTranslation();
  const [unprocessedFilters, setUnprocessedFilter] = useState({
    selectedClients: [],
    selectedStatus: [],
    selectedServiceCategory: [],
    selectedCopyrightType: [],
    createdAtFrom: null,
    createdAtTo: null,
    paymentDueDateFrom: null,
    paymentDueDateTo: null,
  });

  const { data: user, isLoading } = useQuery({ queryKey: 'userMe', queryFn: getUser, retry: false })

  const [selectedClients, setSelectedClients] = useState(currentFilter.selectedClients ?? []);
  const [selectedStatus, setSelectedStatus] = useState(currentFilter.selectedStatus ?? []);
  const [selectedServiceCategory, setSelectedServiceCategory] = useState(currentFilter.selectedServiceCategory ?? []);
  const [selectedCopyrightType, setSelectedCopyrightType] = useState(currentFilter.selectedCopyrightType ?? []);
  
  const [createdAtFrom, setCreatedAtFrom] = useState(
    currentFilter.createdAtPeriod && currentFilter.createdAtPeriod.filter(e => !!e).length === 2 ?
      new Date(currentFilter.createdAtPeriod[0]) :
      null
  );
  const [createdAtTo, setCreatedAtTo] = useState(
    currentFilter.createdAtPeriod && currentFilter.createdAtPeriod.filter(e => !!e).length === 2 ?
      new Date(currentFilter.createdAtPeriod[1]) :
      null
  );
  const [createdAtPeriod, setCreatedAtPeriod] = useState([createdAtFrom, createdAtTo]);
  
  const [paymentDueDateFrom, setPaymentDueDateFrom] = useState(
    currentFilter.paymentDueDatePeriod && currentFilter.paymentDueDatePeriod.filter(e => !!e).length === 2 ?
      new Date(currentFilter.paymentDueDatePeriod[0]) :
      null
  );
  const [paymentDueDateTo, setPaymentDueDateTo] = useState(
    currentFilter.paymentDueDatePeriod && currentFilter.paymentDueDatePeriod.filter(e => !!e).length === 2 ?
      new Date(currentFilter.paymentDueDatePeriod[1]) :
      null
  );
  const [paymentDueDatePeriod, setPaymentDueDatePeriod] = useState([paymentDueDateFrom, paymentDueDateTo]);

  useEffect(() => {
    setUnprocessedFilter({
      selectedClients,
      selectedStatus,
      selectedServiceCategory,
      selectedCopyrightType,
      createdAtTo,
      createdAtFrom,
      paymentDueDateFrom,
      paymentDueDateTo,
      createdAtPeriod,
      paymentDueDatePeriod
    })
  }, [
    selectedClients,
    selectedStatus,
    selectedServiceCategory,
    selectedCopyrightType,
    createdAtPeriod,
    paymentDueDatePeriod
  ]);

  useEffect(() => {
    setCreatedAtPeriod([createdAtFrom, createdAtTo])
  }, [createdAtFrom, createdAtTo])

  useEffect(() => {
    setPaymentDueDatePeriod([paymentDueDateFrom, paymentDueDateTo])
  }, [paymentDueDateFrom, paymentDueDateTo])

  const [open, setOpen] = useState(false);

  const isFilterNotEmpty = (filterData) => {
    return (Object.values(filterData).filter(e => e && (e && e.length > 0)).length > 0)
  }
  
  if (isLoading) {
    return <Loader />
  }
  
  return <BaseDialog
    dialogTitle={t('filter')}
    buttonText={t('filter')}
    open={open}
    setOpen={setOpen}
    icon={isFilterNotEmpty(currentFilter) ? <CheckIcon /> : null}
    ommitStyles={isFilterNotEmpty(currentFilter)}
  >
    <div className="w-[500px] px-2">
      {user.data.roles !== "CLIENT" && <div>
        <MultipleSelectorWithSearch
          fetchOption={(q) => {
            return getClients(10, 0, q)
              .then((result) => {
                return result.data.clients
                  .map(client => {
                    return {
                      label: `${client.firstName} ${client.lastName}`,
                      value: `${client.firstName} ${client.lastName}`,
                      id: client.id
                    }
                  })
              });
          }}
          className="min-h-10 mt-1 mb-2"
          placeholder={t("selected")}
          badgeClassName="bg-emerald-100 hover:bg-emerald-100 text-black"
          value={selectedClients}
          label={t("clients")}
          onChange={(newSelected) => {
            setSelectedClients(newSelected)
          }}
        />
      </div>
      }
      <div>
        <MultipleSelector
          className="min-h-10 mt-1 mb-2"
          placeholder={t("selected")}
          badgeClassName="bg-emerald-100 hover:bg-emerald-100 text-black"
          value={selectedStatus}
          label={t("status")}
          onChange={(newSelected) => {
            setSelectedStatus(newSelected)
          }}
          options={ONE_TIME_CONTRACT_STATUSES(t)}
        />
      </div>
      <div>
        <MultipleSelector
          className="min-h-10 mt-1 mb-2"
          placeholder={t("selected")}
          badgeClassName="bg-emerald-100 hover:bg-emerald-100 text-black"
          value={selectedServiceCategory}
          label={t("contracts:service_category")}
          onChange={(newSelected) => {
            setSelectedServiceCategory(newSelected)
          }}
          options={Object.values(SERVICE_CATEGORIES).map(cat => ({
            value: cat.id,
            label: cat.label
          }))}
        />
      </div>
      <div>
        <MultipleSelector
          className="min-h-10 mt-1 mb-2"
          placeholder={t("selected")}
          badgeClassName="bg-emerald-100 hover:bg-emerald-100 text-black"
          value={selectedCopyrightType}
          label={t("contracts:copyright_type")}
          onChange={(newSelected) => {
            setSelectedCopyrightType(newSelected)
          }}
          options={Object.values(COPYRIGHT_TYPES).map(type => ({
            value: type.id,
            label: type.label
          }))}
        />
      </div>
      <div className="flex justify-between pb-4">
        <div className="w-[48%]">
          <DatePicker date={createdAtFrom}
            setDate={(e) => setCreatedAtFrom(e)}
            label={t("common:created_from")}
          />
        </div>
        <div className="w-[48%]">
          <DatePicker date={createdAtTo}
            setDate={(e) => setCreatedAtTo(e)}
            label={t("common:created_to")}
          />
        </div>
      </div>
      <div className="flex justify-between pb-4">
        <div className="w-[48%]">
          <DatePicker date={paymentDueDateFrom}
            setDate={(e) => setPaymentDueDateFrom(e)}
            label={t("contracts:payment_due_from")}
          />
        </div>
        <div className="w-[48%]">
          <DatePicker date={paymentDueDateTo}
            setDate={(e) => setPaymentDueDateTo(e)}
            label={t("contracts:payment_due_to")}
          />
        </div>
      </div>
      <div className="w-full flex justify-end pt-6">
        <div className="w-full flex justify-end">
          <Button className="mr-4" variant="outline" onClick={() => {
            onFilterChange({})
            setOpen(false)
          }}>{t("common:button_clean_filter")}</Button>
          <Button className="" onClick={() => {
            onFilterChange(unprocessedFilters)
            setOpen(false)
          }}>{t("common:button_save")}</Button>
        </div>
      </div>
    </div>
  </BaseDialog>
}